import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import type { PitchOption, PitchOptionSolution } from "@/lib/pitchAssignmentOptions";
import {
  APPLICABLE_SOLUTION_KINDS,
  solutionConsequence,
} from "@/lib/pitchSolutionConsequences";

export interface PitchConflictModalProps {
  open: boolean;
  option: PitchOption | null;
  loading?: boolean;
  onClose: () => void;
  onApplySolution?: (option: PitchOption, solution: PitchOptionSolution) => void;
}

export function PitchConflictModal({
  open,
  option,
  loading = false,
  onClose,
  onApplySolution,
}: PitchConflictModalProps) {
  if (!option) return null;

  const applicableSolutions = option.solutions.filter((solution) =>
    APPLICABLE_SOLUTION_KINDS.includes(
      solution.kind as (typeof APPLICABLE_SOLUTION_KINDS)[number],
    ),
  );

  const footer = (
    <div className="flex flex-wrap gap-2 sm:justify-end">
      <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
        Schließen
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Stellplatz ${option.code} nicht direkt verfügbar`}
      description="Konflikte und mögliche Lösungen für diese manuelle Zuweisung"
      size="lg"
      footer={footer}
    >
      <div className="space-y-5">
        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-label">Probleme</h4>
          {option.problems.length === 0 ? (
            <p className="text-sm text-label-secondary">Keine Probleme erkannt.</p>
          ) : (
            <ul className="space-y-2">
              {option.problems.map((problem, index) => (
                <li
                  key={`${problem.kind}-${index}`}
                  className="rounded-lg border border-status-orange/30 bg-status-orange/10 px-3 py-2 text-sm text-label"
                >
                  {problem.message}
                </li>
              ))}
            </ul>
          )}
        </section>

        {option.relocation_plan.length > 0 ? (
          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-label">Geplante Umplanung</h4>
            <p className="text-xs text-label-secondary">
              Diese Buchungen würden automatisch auf andere Plätze verschoben, damit der gewählte
              Stellplatz frei wird.
            </p>
            <ul className="space-y-2">
              {option.relocation_plan.map((step) => (
                <li
                  key={step.booking_id}
                  className="rounded-lg border border-separator bg-fill-tertiary/40 px-3 py-2 text-sm text-label"
                >
                  Buchung {step.booking_number ?? step.booking_id.slice(0, 8)} → Platz{" "}
                  {step.to_pitch_code ?? "?"}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {applicableSolutions.length > 0 ? (
          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-label">Lösungsvorschläge</h4>
            <p className="text-xs text-label-secondary">
              Jede Lösung zeigt die Folgen. Doppelbelegungen werden dabei verhindert – fixierte
              oder eingecheckte Gäste blockieren weiterhin.
            </p>
            <ul className="space-y-2">
              {applicableSolutions.map((solution, index) => {
                const consequence = solutionConsequence(solution);
                return (
                  <li
                    key={`${solution.kind}-${solution.booking_id ?? index}`}
                    className="flex flex-col gap-2 rounded-lg border border-separator bg-fill-tertiary/40 px-3 py-2 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-label">{solution.message}</p>
                      {consequence ? (
                        <p className="text-xs text-label-secondary">{consequence}</p>
                      ) : null}
                    </div>
                    {onApplySolution ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        loading={loading}
                        className="shrink-0"
                        onClick={() => onApplySolution(option, solution)}
                      >
                        Anwenden
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : (
          <p className="text-sm text-label-secondary">
            Für diesen Stellplatz gibt es keine automatisch anwendbare Lösung. Bitte beheben Sie die
            Konflikte manuell (z. B. fixierte Buchung umplanen oder Sperrzeitraum anpassen).
          </p>
        )}

        {option.solutions.some((solution) => solution.kind === "unfix_booking") ? (
          <section className="space-y-2">
            <h4 className="text-sm font-semibold text-label">Manuelle Schritte</h4>
            <ul className="space-y-2">
              {option.solutions
                .filter((solution) => solution.kind === "unfix_booking")
                .map((solution, index) => (
                  <li
                    key={`manual-${solution.booking_id ?? index}`}
                    className="rounded-lg border border-separator px-3 py-2 text-sm text-label-secondary"
                  >
                    <p>{solution.message}</p>
                    {solutionConsequence(solution) ? (
                      <p className="mt-1 text-xs">{solutionConsequence(solution)}</p>
                    ) : null}
                  </li>
                ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
